/**
 * Confirms acquiring edit rights on a plan.
 *
 * A user holds one lock globally, so when this browser knows of a session on
 * another plan the same modal spells out what is about to end, rather than
 * stacking a second confirmation on top of this one.
 */
import type { FC } from 'react';

import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';

import { TPO_EDIT_PLAN_CONFIRM_MODAL, TPO_EDIT_PLAN_TAKEOVER_NOTE } from '../../../config/testIds';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import type { LastAcquiredPlanRef } from '../types';

export interface EditPlanConfirmModalProps {
    open: boolean;
    planName: string;
    /** A session on a DIFFERENT plan that acquiring here will end. */
    otherPlan: LastAcquiredPlanRef | null;
    isAcquiring: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const EditPlanConfirmModal: FC<EditPlanConfirmModalProps> = ({
    open,
    planName,
    otherPlan,
    isAcquiring,
    onCancel,
    onConfirm,
}) => {
    const { labels } = useTpoGenConfig();
    const editSession = labels.editSession;

    const description = (
        <div className="bg-[#fafafa] rounded-lg p-2 flex flex-col gap-y-2">
            <p className="text-sm text-[#18181b]">{editSession.editPlanQuestion.replace('{planName}', planName)}</p>
            {otherPlan ? (
                <p className="text-sm text-[#B26A00]" data-testid={TPO_EDIT_PLAN_TAKEOVER_NOTE}>
                    {editSession.takeOverNote.replace('{planName}', otherPlan.planName || otherPlan.planId)}
                </p>
            ) : null}
        </div>
    );

    return (
        <ConfirmationModal
            data-testid={TPO_EDIT_PLAN_CONFIRM_MODAL}
            open={open}
            onCancel={onCancel}
            onSubmitClick={onConfirm}
            okText={editSession.confirmYes}
            cancelText={labels.common.cancel}
            okButtonProps={{ type: 'primary', loading: isAcquiring, style: { borderRadius: 6 } }}
            loading={isAcquiring}
            title={editSession.editPlanTitle}
            description={description}
            width={520}
        />
    );
};

EditPlanConfirmModal.displayName = 'EditPlanConfirmModal';
export default EditPlanConfirmModal;
